import { Calculator, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/**
 * CalculatePayrollPreview — rich preview for CALCULATE_PAYROLL actions
 * (tool: calcular_nomina). Shows bruto/SS/IRPF/neto in a structured table
 * before user confirms generation.
 */
export default function CalculatePayrollPreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <Calculator className="w-3.5 h-3.5" />
        <span>Nómina aprobada — generando borrador…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Generación de nómina cancelada</span>
      </div>
    );
  }

  const ss   = preview?.deducciones?.seguridad_social ?? 0;
  const irpf = preview?.deducciones?.irpf ?? 0;
  const irpfPct = preview?.deducciones?.tipo_irpf ?? '—';

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight truncate">
            {preview?.empleado ?? '—'}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {preview?.periodo} · {preview?.num_pagas} pagas · grupo {preview?.grupo_cotizacion}
          </p>
        </div>
        <span className="text-lg font-bold text-indigo-700 dark:text-indigo-300 shrink-0 tabular-nums">
          {formatEur(preview?.neto)}
        </span>
      </div>

      <div className="text-xs space-y-1 border-t border-indigo-200 dark:border-indigo-800 pt-2">
        <div className="flex justify-between text-muted-foreground">
          <span>Bruto mensual</span>
          <span className="tabular-nums">{formatEur(preview?.bruto_mensual)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>− SS trabajador</span>
          <span className="tabular-nums">−{formatEur(ss)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>− IRPF ({irpfPct})</span>
          <span className="tabular-nums">−{formatEur(irpf)}</span>
        </div>
        <div className="flex justify-between font-semibold text-foreground pt-1 border-t border-indigo-100 dark:border-indigo-900/40">
          <span>Neto a percibir</span>
          <span className="tabular-nums">{formatEur(preview?.neto)}</span>
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={onApprove} className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
          <Calculator className="w-3 h-3 mr-1" />Generar nómina
        </Button>
      </div>
    </div>
  );
}
